/**
 * @file src/helpers/ory/verificationCodeAnalytics.ts
 * @exports getCodeFailureProperties, recordCodeSent
 * @summary Remembers when each login or registration code was sent, and builds the properties for a rejected code.
 * @remarks Sends are kept per flow in sessionStorage because the code page is reached, and re-reached on resend, by
 *          a redirect. Kratos deletes a flow's earlier codes when it sends a new one, so only the latest send's age
 *          matters. Never includes the code or any message text.
 */

import type { AnalyticsAuthFailReason } from "../analyticsEvents";
import { getOryErrorMessageId, getOryFailReason } from "./getOryFailReason";

const STORAGE_PREFIX = "verification_code_sends:";

type CodeSends = { codesSent: number; sentAt: number };

type FlowRef = { flowId: string | undefined };

type CodeFailureArgs = FlowRef & { error: unknown };

type CodeFailureProperties = {
	codeAgeSeconds?: number;
	codesSent?: number;
	oryMessageId?: number;
	reason: AnalyticsAuthFailReason;
};

const readSends = ({ flowId }: FlowRef): CodeSends | undefined => {
	if (typeof window === "undefined" || !flowId) return undefined;
	try {
		const stored = sessionStorage.getItem(STORAGE_PREFIX + flowId);
		return stored ? (JSON.parse(stored) as CodeSends) : undefined;
	} catch {
		return undefined;
	}
};

/** Records that Kratos confirmed sending a code on this flow. */
export const recordCodeSent = ({ flowId }: FlowRef): void => {
	if (typeof window === "undefined" || !flowId) return;
	const sends: CodeSends = { codesSent: (readSends({ flowId })?.codesSent ?? 0) + 1, sentAt: Date.now() };
	try {
		sessionStorage.setItem(STORAGE_PREFIX + flowId, JSON.stringify(sends));
	} catch {
		// Storage can be unavailable (private mode, quota); the event simply goes without timing.
	}
};

/** Properties for a failed code event: why it failed and how old the code was, when the send was recorded. */
export const getCodeFailureProperties = ({ error, flowId }: CodeFailureArgs): CodeFailureProperties => {
	const sends = readSends({ flowId });
	return {
		codeAgeSeconds: sends ? Math.round((Date.now() - sends.sentAt) / 1000) : undefined,
		codesSent: sends?.codesSent,
		oryMessageId: getOryErrorMessageId(error),
		reason: getOryFailReason(error),
	};
};
